/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, createRef } from 'react';
import { Tree, ControlledTreeEnvironment } from 'react-complex-tree';
import 'react-complex-tree/lib/style.css';
import {
  Spinner,
  Button as ButtonSops,
  ButtonGroup
} from '@superops/ui-design-system';
import { BackgroundIcon, withErrorBoundary } from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import styled from 'styled-components';
import { cloneDeep, isEqual } from 'lodash';
import { SVGIcon } from '@superops/super-icons';

const TreeContainer = styled.div`
  cursor: pointer;
  position: absolute;

  .rct-tree-item-li {
    font-size: 16px !important;
  }

  .rct-tree-items-container,
  .rct-tree-root {
    background: white !important;
    width: 100%;
    outline: none !important;
  }
`;

const Button = styled.button`
  border: none;
  background: transparent;
  flex: 1;
`;

const ChildContainer = styled.div`
  padding-left: 10px;
  margin-left: 9px;
  background-image: ${(props) =>
    `linear-gradient(black ${
      props?.isActive ? '40%' : '11%'
    }, rgba(255, 255, 255, 0) 0%)`};
  background-position: left;
  background-size: 1px 3px;
  background-repeat: repeat-y;
`;

const ControlledTreeComponent = withErrorBoundary(
  ({
    contentData,
    currentPath,
    titleTemplate: TitleTemplate,
    handleCreateEdit,
    getValues,
    onOpen,
    onClose,
    onItemClick,
    openFolder,
    createNewCard,
    folderToBeOpened,
    isSearchLoading,
    updateChildToShow
  }) => {
    const [focusedItem, setFocusedItem] = useState();
    const [expandedItems, setExpandedItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [renameMode, setRenameMode] = useState(false);
    const [childLoading, setChildLoading] = useState(false);
    const treeElement = document.getElementsByClassName(
      'sops-tree-root-registry'
    );
    const [refMap, setRefMap] = useState(
      (function() {
        const _refMap = {};
        Object.keys(contentData).forEach((content) => {
          _refMap[content] = createRef();
        });
        return _refMap;
      })()
    );

    const scrollIntoView = (givenPath) => {
      const section = givenPath || [...currentPath].reverse().join('---');
      const treeRect = treeElement[0].getBoundingClientRect();
      const top = refMap[section]?.current?.getBoundingClientRect()?.top;
      const bottom = refMap[section]?.current?.getBoundingClientRect()?.bottom;

      if (
        !(top >= 0 && bottom <= treeRect?.bottom && top >= treeRect?.top) &&
        refMap[section]?.current
      ) {
        refMap[section].current.scrollIntoView();
      }
    };

    function getUniqueIndex(value) {
      return value.split('---')[0];
    }

    const customArrow = ({ context, item }) => {
      const { arrowProps, isExpanded } = context;
      const { hasChildren, index, children } = item;

      function toggleClick() {
        if (!isExpanded) {
          onOpen(item);
          setExpandedItems([...expandedItems, index]);
          // if (!children) {
          //   setChildLoading(true);
          //   setTimeout(() => {
          //     setChildLoading(false);
          //   }, 1500);
          // }
        }
        if (isExpanded) {
          onClose(item);
          arrowProps.onClick();
          setExpandedItems(expandedItems.filter((exp) => exp !== index));
        }
      }

      if (isExpanded) {
        return hasChildren ? (
          <div
            className="fx center"
            style={{ opacity: '0.8' }}
            onClick={toggleClick}
          >
            <SVGIcon name="ArrowDown" />
          </div>
        ) : (
          <Spinner size="small" />
        );
      }
      return (
        <div
          className="fx center"
          style={{ opacity: '0.6' }}
          onClick={toggleClick}
        >
          <SVGIcon name="ArrowRight" />
        </div>
      );
    };

    function handleOnClick(item) {
      onItemClick(item);
      setFocusedItem(item.index);
    }

    function handleOnDoubleClick(item) {
      setExpandedItems([...expandedItems, item.index]);
    }

    useEffect(() => {
      const foldersToBeOpened = folderToBeOpened?.filter(
        (folder) => expandedItems.indexOf(folder) === -1
      );
      setExpandedItems([...expandedItems, ...foldersToBeOpened]);
      const _refMap = {};
      Object.keys(contentData).forEach((content) => {
        _refMap[content] = createRef();
      });
      setRefMap(_refMap);
    }, [contentData]);

    useEffect(() => {
      if (!isEqual(expandedItems, folderToBeOpened)) {
        openFolder(expandedItems);
      }
    }, [expandedItems]);

    useEffect(() => {
      const clonedCurrentPath = [...currentPath]
        .reverse()
        .join('---')
        .toLowerCase();
      setFocusedItem(
        clonedCurrentPath.length > 0 ? clonedCurrentPath : currentPath
      );
      if (clonedCurrentPath.length > 0) scrollIntoView(clonedCurrentPath);
    }, [currentPath]);

    // useEffect(() => {
    //   let clonedCurrentPath = [...currentPath].reverse().join('---');
    //   clonedCurrentPath.length > 0 && scrollIntoView(clonedCurrentPath);
    // }, []);

    return (
      <TreeContainer>
        <ControlledTreeEnvironment
          items={contentData}
          getItemTitle={(item) => item.data}
          viewState={{
            'tree-2': {
              focusedItem,
              expandedItems,
              selectedItems
            }
          }}
          renderItemTitle={({ title, item, context }) => (
            <TitleTemplate
              name={title}
              item={item}
              isExpanded={context.isExpanded}
              isActive={focusedItem === item.index}
              onClick={() => handleOnClick(item)}
              onDoubleClick={() => handleOnDoubleClick(item)}
              renameMode={renameMode}
              setRenameMode={setRenameMode}
              createNewCard={createNewCard}
              handleCreateEdit={handleCreateEdit}
              isSearchLoading={isSearchLoading}
            />
          )}
          renderDepthOffset={40}
          renderItem={({
            title,
            arrow,
            depth,
            context,
            item,
            info,
            children
          }) => (
            <div
              style={{
                margin: 0,
                display: 'flex',
                width: '100%',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}
              ref={refMap[item.index]}
            >
              <div className="fx full-width full-height actr">
                {arrow}
                <Button>{title}</Button>
              </div>
              <ChildContainer
                isActive={focusedItem === item.index}
                className="grid full-width full-height"
              >
                {children}
                {context.isExpanded &&
                  item.childrenLength > item.noOfChildToShow && (
                    <ButtonGroup>
                      <ButtonSops
                        className="ta-l cursor-pointer"
                        onClick={() => updateChildToShow(item.index)}
                        plain
                      >
                        load more
                      </ButtonSops>
                    </ButtonGroup>
                  )}
              </ChildContainer>
            </div>
          )}
          onFocusItem={(item) => {
            setFocusedItem(item.index);
          }}
          onExpandItem={(item, children) => {
            setExpandedItems([...expandedItems, item.index]);
          }}
          onPrimaryAction={(item) => {
            getValues([...item.parent, item.data]);
            setExpandedItems([...expandedItems, item.index]);
          }}
          onCollapseItem={(item) => {
            setExpandedItems(
              [...expandedItems].filter(
                (expandedItemIndex) =>
                  expandedItemIndex !== getUniqueIndex(item.index)
              )
            );
          }}
          canSearch={false}
          canSearchByStartingTyping={false}
          onSelectItems={(items) => {
            setSelectedItems(items);
          }}
          onMissingItems={(items) => console.log(items)}
          keyboardBindings={
            !renameMode
              ? {
                  primaryAction: ['ArrowRight']
                }
              : false
          }
        >
          <Tree
            renderItemArrow={customArrow}
            treeId="tree-2"
            rootItem="root"
            treeLabel="Tree Example"
          />
        </ControlledTreeEnvironment>
      </TreeContainer>
    );
  }
);

const UncontrolledTreeComponent = withErrorBoundary(
  ({
    contentData,
    currentPath,
    titleTemplate: TitleTemplate,
    options,
    openFolder,
    onOpen,
    onClose,
    onItemClick,
    actions,
    folderToBeOpened,
    isActionAllowed,
    treeRef,
    updateChildToShow,
    showLoadMore
  }) => {
    const [focusedItem, setFocusedItem] = useState();
    const [expandedItems, setExpandedItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [renameMode, setRenameMode] = useState(false);
    // const treeElement = document.getElementsByClassName('sops-tree-root');
    const [refMap, setRefMap] = useState(
      (function() {
        const _refMap = {};
        Object.keys(contentData).forEach((content) => {
          _refMap[content] = createRef();
        });
        return _refMap;
      })()
    );

    const customArrow = ({ context, item, info }) => {
      const { arrowProps, isExpanded } = context;
      const { hasChildren, index, children } = item;
      const [childLoading, setChildLoading] = useState(false);
      const isActive = focusedItem === item.index;

      function toggleClick() {
        arrowProps.onClick();
        if (isExpanded) {
          onClose(item);
          setExpandedItems(
            [...expandedItems].filter(
              (expandedItemIndex) => expandedItemIndex !== item.index
            )
          );
        } else {
          setFocusedItem(item.index);
          if (!isActive) onOpen(item);
          setExpandedItems([...expandedItems, item.index]);
          if (!children) {
            setChildLoading(true);
            setTimeout(() => {
              setChildLoading(false);
            }, 1500);
          }
        }
      }

      if (isExpanded) {
        return !childLoading ? (
          <div
            className="fx center"
            style={{ opacity: '0.8' }}
            onClick={toggleClick}
          >
            <SVGIcon name="ArrowDown" />
          </div>
        ) : (
          <Spinner size="small" />
        );
      }
      return (
        <div
          className="fx center"
          style={{ opacity: '0.6' }}
          onClick={toggleClick}
        >
          <SVGIcon name="ArrowRight" />
        </div>
      );
    };

    const scrollIntoView = (givenPath) => {
      const section = givenPath || [...currentPath].reverse().join('---');
      const treeRect = treeRef?.current?.getBoundingClientRect();
      const top = refMap[section]?.current?.getBoundingClientRect()?.top;
      const bottom = refMap[section]?.current?.getBoundingClientRect()?.bottom;

      if (
        !(top >= 0 && bottom <= treeRect?.bottom && top >= treeRect?.top) &&
        refMap[section]?.current
      ) {
        refMap[section].current.scrollIntoView({ inline: 'start' });
        // console.log('inside view port');
      }
    };

    function handleOnClick(item) {
      onItemClick(item);
      setFocusedItem(item.index);
    }

    function handleOnDoubleClick(item) {
      setExpandedItems([...expandedItems, item.index]);
    }

    useEffect(() => {
      if (currentPath.length > 0) {
        const foldersToBeOpened = folderToBeOpened?.filter(
          (folder) => expandedItems.indexOf(folder) === -1
        );
        setExpandedItems([...expandedItems, ...foldersToBeOpened]);
      } else {
        setExpandedItems([]);
      }

      const _refMap = {};
      Object.keys(contentData).forEach((content) => {
        _refMap[content] = createRef();
      });
      setRefMap(_refMap);
    }, [contentData, currentPath]);

    useEffect(() => {
      if (!isEqual(expandedItems, folderToBeOpened)) {
        openFolder(expandedItems);
      }
      // scrollIntoView();
    }, [expandedItems]);

    useEffect(() => {
      const clonedCurrentPath = [...currentPath]
        .reverse()
        .join('---')
        .toLowerCase();
      setFocusedItem(
        clonedCurrentPath.length > 0 ? clonedCurrentPath : currentPath
      );
      if (clonedCurrentPath.length > 0) scrollIntoView(clonedCurrentPath);
    }, [currentPath]);

    return (
      <TreeContainer>
        <ControlledTreeEnvironment
          items={contentData}
          getItemTitle={(item) => item.data}
          viewState={{
            'tree-1': {
              focusedItem,
              expandedItems,
              selectedItems
            }
          }}
          renderItemTitle={({ title, item, context }) => {
            const { isExpanded } = context;
            return (
              <TitleTemplate
                name={title}
                item={item}
                isExpanded={isExpanded}
                isActive={focusedItem === item.index}
                onClick={() => handleOnClick(item)}
                onDoubleClick={() => handleOnDoubleClick(item)}
                renameMode={renameMode}
                setRenameMode={setRenameMode}
                options={options}
                actions={actions}
                isActionAllowed={isActionAllowed}
              />
            );
          }}
          renderItem={({
            title,
            arrow,
            depth,
            context,
            item,
            info,
            children
          }) => {
            return (
              <div
                style={{
                  margin: 0,
                  display: 'flex',
                  width: '100%',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
                ref={refMap[item.index]}
              >
                <div className="fx full-width full-height actr">
                  {arrow}
                  <Button>{title}</Button>
                </div>
                <ChildContainer
                  isActive={focusedItem === item.index}
                  className="grid full-width full-height"
                >
                  {children}
                  {context.isExpanded &&
                    item.childrenLength > item.noOfChildToShow && (
                      <ButtonGroup>
                        <ButtonSops
                          className="ta-l cursor-pointer"
                          onClick={() => updateChildToShow(currentPath, item)}
                          plain
                        >
                          load more
                        </ButtonSops>
                      </ButtonGroup>
                    )}
                </ChildContainer>
              </div>
            );
          }}
          onFocusItem={(item) => {
            // console.log('focusedItem item', item);
            scrollIntoView(item.index);
            setFocusedItem(item.index);
          }}
          onExpandItem={(item) => {
            // console.log('item => ', item);
            setExpandedItems([...expandedItems, item.index]);
          }}
          onPrimaryAction={(item) => {
            setExpandedItems([...expandedItems, item.index]);
            setFocusedItem(item.index);
            onItemClick(item);
          }}
          onCollapseItem={(item) => {
            setExpandedItems(
              [...expandedItems].filter(
                (expandedItemIndex) => expandedItemIndex !== item.index
              )
            );
          }}
          canSearch={false}
          canSearchByStartingTyping={false}
          keyboardBindings={
            !renameMode
              ? {
                  primaryAction: ['ArrowRight']
                }
              : null
          }
        >
          <Tree
            renderItemArrow={customArrow}
            treeId="tree-1"
            rootItem="root"
            treeLabel="Tree"
          />
        </ControlledTreeEnvironment>
      </TreeContainer>
    );
  }
);

export { ControlledTreeComponent, UncontrolledTreeComponent };
